import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Empty, Typography } from "antd";

import MultitrackTimelineCanvas from "../../../timeline/MultitrackTimelineCanvas";
import { buildTimelineVisualItems } from "../../../lib/canvas/timelineVisualSemantics";
import { formatTrackTypeLabel, getTrackDisplayName, getTrackFlights, toArray } from "../../../lib/trackUtils";
import { buildItemTimeLabels, buildItemTimeTooltipLines, getTimeWindowFromFlights, normalizeString } from "../../../lib/canvas/scheduleDraftCanvasHelpers";
import { getFlightTimeDisplayInfo } from "../../../lib/flightTimingUtils";
import { debugScheduleDraftTimeline } from "../../../lib/timelineDebug";
import "./TimelineCompareView.css";

const { Text } = Typography;

const EDITING_TRACK_ID = "compare:editing";
const COMPARE_CORRIDOR_TRACK_ID = "compare:link-corridor";
const REFERENCE_TRACK_ID = "compare:reference";

const ITEM_HOVER_CLASSES = [
    "schedule-draft-timeline-item-hover-active",
    "schedule-draft-timeline-item-hover-related",
    "schedule-draft-timeline-item-hover-muted",
];

const PUBLICATION_STATUS_LABEL = {
    MATCHED: "已替换",
    INVALID: "发布快照缺失",
    NEW: "已发布新增",
    CANCELLED: "已发布取消",
    IGNORE: "已发布忽略",
    UNMATCHED: "发布快照缺失",
};

const MATCH_STATUS_LABEL = {
    MATCHED: "已匹配",
    INVALID: "失效",
    NEW: "新增",
    CANCELLED: "取消",
    IGNORE: "忽略",
    UNMATCHED: "待处理",
};

const MATCH_TYPE_LABEL = {
    AUTO: "自动",
    MANUAL: "人工",
};

const TIME_DIFF_NEUTRAL_THRESHOLD_MINUTES = 1;
const EXECUTION_STATUS_SAFE_FOR_MATCH = new Set(["SCHEDULED", "ACTIVE", "DELAYED", "DIVERTED"]);
const COMPARE_MEASURE_SETTLE_DELAYS_MS = [80, 220, 420];
const COMPARE_LAYOUT_RETRY_DELAY_MS = 260;
const COMPARE_LAYOUT_RETRY_LIMIT = 3;
const EMPTY_MATCHES = [];
const EMPTY_AIRPORT_CONSTRAINTS_MAP = {};

function asKey(value) {
    return value == null ? null : String(value);
}

function getEditingItemId(flightId) {
    return `compare-editing:${flightId}`;
}

function getReferenceItemId(sourceId) {
    return `compare-reference:${sourceId}`;
}

export function getReferenceFlightSourceId(flight) {
    return asKey(flight?.source_operation_id ?? flight?.source_flight_id ?? flight?.id);
}

function getMatchSourceId(match) {
    return asKey(match?.source_operation_id ?? match?.source_flight_id);
}

function getMatchDraftFlightId(match) {
    return asKey(match?.schedule_draft_flight_id);
}

function getMatchKey(match) {
    if (!match) {
        return null;
    }
    return asKey(match.id) || `${getMatchDraftFlightId(match) || "-"}:${getMatchSourceId(match) || "-"}`;
}

function getMatchReferenceTrackId(match) {
    return asKey(match?.reference_track_id);
}

function getMatchStatus(match, { publicationReview = false } = {}) {
    if (publicationReview) {
        return String(match?.match_status || "UNMATCHED").toUpperCase();
    }
    if (match?.is_valid === false) {
        return "INVALID";
    }
    return String(match?.effective_status || match?.match_status || "UNMATCHED").toUpperCase();
}

function getRawMatchStatus(match) {
    return String(match?.match_status || "UNMATCHED").toUpperCase();
}

function getMatchType(match) {
    return match ? String(match?.match_type || "AUTO").toUpperCase() : null;
}

function getMatchStatusDisplayLabel(status, { publicationReview = false } = {}) {
    if (publicationReview) {
        return PUBLICATION_STATUS_LABEL[status] || "发布快照缺失";
    }
    return MATCH_STATUS_LABEL[status] || "待处理";
}

function getMatchTypeDisplayLabel(matchType) {
    return MATCH_TYPE_LABEL[matchType] || "决策待确认";
}

function getMatchSourceTone(match) {
    return getMatchType(match) === "MANUAL" ? "manual" : "auto";
}

function getDateTimeDiffMinutes(leftValue, rightValue) {
    if (!leftValue || !rightValue) {
        return null;
    }
    const leftTime = new Date(leftValue).getTime();
    const rightTime = new Date(rightValue).getTime();
    if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) {
        return null;
    }
    return Math.round((leftTime - rightTime) / 60000);
}

function getTimeDiffDirection(minutes) {
    if (minutes === null || minutes === undefined || Math.abs(minutes) <= TIME_DIFF_NEUTRAL_THRESHOLD_MINUTES) {
        return "neutral";
    }
    return minutes > 0 ? "delayed" : "early";
}

function formatRelativeMinutes(minutes) {
    const absoluteMinutes = Math.abs(Number(minutes) || 0);
    const hours = Math.floor(absoluteMinutes / 60);
    const mins = absoluteMinutes % 60;
    if (hours && mins) {
        return `${hours}h${mins}m`;
    }
    if (hours) {
        return `${hours}h`;
    }
    return `${mins}m`;
}

function formatTimeDiffLabel(minutes) {
    if (minutes === null || minutes === undefined) {
        return null;
    }
    const direction = getTimeDiffDirection(minutes);
    if (direction === "neutral") {
        return null;
    }
    return `${direction === "delayed" ? "+" : "-"}${formatRelativeMinutes(minutes)}`;
}

function getCompareTimeDiffs(editingFlight, referenceFlight) {
    const editingTimeInfo = getFlightTimeDisplayInfo(editingFlight);
    const referenceTimeInfo = getFlightTimeDisplayInfo(referenceFlight, { useEffectiveTime: true });
    return {
        departureDiffMinutes: getDateTimeDiffMinutes(editingTimeInfo.departureTime, referenceTimeInfo.departureTime),
        arrivalDiffMinutes: getDateTimeDiffMinutes(editingTimeInfo.arrivalTime, referenceTimeInfo.arrivalTime),
    };
}

function getPrimaryTimeDiffMinutes({ departureDiffMinutes, arrivalDiffMinutes } = {}) {
    return departureDiffMinutes ?? arrivalDiffMinutes ?? null;
}

function getReferenceExecutionStatus(flight) {
    return String(flight?.source_metadata?.status || flight?.status || "").toUpperCase();
}

function hasExecutionStatusRisk(flight) {
    const status = getReferenceExecutionStatus(flight);
    return Boolean(status && !EXECUTION_STATUS_SAFE_FOR_MATCH.has(status));
}

function getTransactionSummaryTotal(summary) {
    return Number(summary?.total) || 0;
}

function getObjectEntrySignature(value) {
    if (!value || typeof value !== "object") {
        return "";
    }
    return Object.entries(value)
        .sort(([leftKey], [rightKey]) => String(leftKey).localeCompare(String(rightKey)))
        .map(([key, entryValue]) => `${key}:${entryValue}`)
        .join(",");
}

function getTransactionSummarySignature(summary) {
    if (!summary) {
        return "";
    }
    return [
        asKey(summary.flight_id ?? summary.source_operation_id ?? summary.operation_id) || "",
        getTransactionSummaryTotal(summary),
        getObjectEntrySignature(summary.by_type),
        getObjectEntrySignature(summary.by_migration_status),
    ].join("~");
}

function getSummaryItems(value) {
    return Array.isArray(value) ? value : toArray(value?.items || value?.summaries || value?.data?.items);
}

function normalizeTransactionSummaryMap(transactionSummaries) {
    return new Map(
        getSummaryItems(transactionSummaries)
            .map((item) => [asKey(item?.flight_id ?? item?.source_operation_id ?? item?.operation_id), item])
            .filter(([key]) => key),
    );
}

function getTransactionSummaryMapSignature(transactionSummaryBySourceId) {
    return Array.from(transactionSummaryBySourceId.entries())
        .sort(([leftKey], [rightKey]) => String(leftKey).localeCompare(String(rightKey)))
        .map(([key, summary]) => `${key}:${getTransactionSummarySignature(summary)}`)
        .join("|");
}

export function buildTimelineCompareLinkDiff(match, editingFlight, referenceFlight, transactionSummary = null, { publicationReview = false } = {}) {
    const timeDiffs = getCompareTimeDiffs(editingFlight, referenceFlight);
    const primaryDiffMinutes = getPrimaryTimeDiffMinutes(timeDiffs);
    const timeDirection = getTimeDiffDirection(primaryDiffMinutes);
    const timeLabel = formatTimeDiffLabel(primaryDiffMinutes);
    const status = getMatchStatus(match, { publicationReview });
    const invalid = !publicationReview && (status === "INVALID" || match?.is_valid === false);
    const risk = hasExecutionStatusRisk(referenceFlight) || getTransactionSummaryTotal(transactionSummary) > 0;
    const tone = invalid ? "invalid" : risk ? "risk" : timeDirection;

    return {
        ...timeDiffs,
        primaryDiffMinutes,
        timeDirection,
        tone,
        label: timeLabel,
    };
}

function isLineMatch(match, { publicationReview = false } = {}) {
    const rawStatus = getRawMatchStatus(match);
    const effectiveStatus = getMatchStatus(match, { publicationReview });
    return Boolean(
        getMatchDraftFlightId(match)
            && getMatchSourceId(match)
            && ["MATCHED", "INVALID"].includes(rawStatus === "UNMATCHED" ? effectiveStatus : rawStatus),
    );
}

function buildHoverFocusFromMatch(match, origin = "link") {
    return {
        origin,
        itemId: null,
        matchKey: getMatchKey(match),
        editingFlightId: getMatchDraftFlightId(match),
        referenceSourceId: getMatchSourceId(match),
    };
}

function buildHoverFocusFromItem(item) {
    const role = item?.meta?.role;
    const match = item?.meta?.match || null;
    const publicationReview = Boolean(item?.meta?.publicationReview);
    const linkedMatch = isLineMatch(match);
    const status = getMatchStatus(match, { publicationReview });
    const statusLabel = getStatusLabel(match, publicationReview ? "发布快照缺失" : "待处理", { publicationReview });
    return {
        origin: "item",
        itemId: asKey(item?.id),
        matchKey: linkedMatch ? getMatchKey(match) : null,
        editingFlightId: linkedMatch ? getMatchDraftFlightId(match) : null,
        referenceSourceId: linkedMatch ? getMatchSourceId(match) : null,
        status,
        statusLabel,
        statusDescription: getStatusDescription(status, role, { publicationReview }),
        title: item?.title,
    };
}

function getOppositeCompareItem(item, itemById) {
    const role = item?.meta?.role;
    const match = item?.meta?.match;
    if (!isLineMatch(match)) {
        return null;
    }

    if (role === "editing") {
        return itemById.get(getReferenceItemId(getMatchSourceId(match))) || null;
    }
    if (role === "reference") {
        return itemById.get(getEditingItemId(getMatchDraftFlightId(match))) || null;
    }
    return null;
}

function buildTransactionHoverContext(sourceId, transactionSummaryBySourceId, transactionSummaryLoading, transactionSummaryError) {
    const sourceKey = asKey(sourceId);
    return {
        transactionSummary: sourceKey ? transactionSummaryBySourceId.get(sourceKey) || null : null,
        transactionSummaryLoading,
        transactionSummaryError,
    };
}

function buildCompareHoverTargetFromItem(item, itemById, transactionContext = {}) {
    if (!item) {
        return null;
    }
    const role = item?.meta?.role;
    const oppositeItem = getOppositeCompareItem(item, itemById);
    return {
        mode: "compare",
        kind: "item",
        item,
        match: item?.meta?.match || null,
        editingItem: role === "editing" ? item : oppositeItem,
        referenceItem: role === "reference" ? item : oppositeItem,
        oppositeItem,
        publicationReview: Boolean(item?.meta?.publicationReview),
        ...transactionContext,
    };
}

function buildCompareHoverTargetFromMatch(match, itemById, transactionContext = {}) {
    if (!match) {
        return null;
    }
    const editingItem = itemById.get(getEditingItemId(getMatchDraftFlightId(match))) || null;
    const referenceItem = itemById.get(getReferenceItemId(getMatchSourceId(match))) || null;
    return {
        mode: "compare",
        kind: "link",
        item: editingItem || referenceItem,
        match,
        editingItem,
        referenceItem,
        oppositeItem: referenceItem || editingItem,
        publicationReview: Boolean(editingItem?.meta?.publicationReview || referenceItem?.meta?.publicationReview),
        ...transactionContext,
    };
}

function getViewportPointFromReactEvent(event) {
    const x = Number(event?.clientX);
    const y = Number(event?.clientY);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function toFiniteNumber(value) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
}

function normalizeViewportRect(rect) {
    const left = toFiniteNumber(rect?.left ?? rect?.x);
    const top = toFiniteNumber(rect?.top ?? rect?.y);
    const width = toFiniteNumber(rect?.width);
    const height = toFiniteNumber(rect?.height);
    const right = toFiniteNumber(rect?.right) ?? (left !== null && width !== null ? left + width : null);
    const bottom = toFiniteNumber(rect?.bottom) ?? (top !== null && height !== null ? top + height : null);

    if (left === null || top === null || right === null || bottom === null) {
        return null;
    }

    return {
        left,
        top,
        right,
        bottom,
        width: width ?? Math.max(0, right - left),
        height: height ?? Math.max(0, bottom - top),
    };
}

function getViewportPointFromAnchorRect(anchorRect) {
    if (!anchorRect) {
        return null;
    }
    return {
        x: anchorRect.left + anchorRect.width / 2,
        y: anchorRect.top + anchorRect.height / 2,
    };
}

function withHoverViewportContext(target, event, trackRowHeight, anchorElement = null) {
    if (!target) {
        return null;
    }
    const anchorRect = normalizeViewportRect(anchorElement?.getBoundingClientRect?.());
    return {
        ...target,
        viewportPoint: getViewportPointFromAnchorRect(anchorRect) || getViewportPointFromReactEvent(event),
        anchorRect,
        anchorElement: anchorElement || null,
        trackRowHeight,
    };
}

function getCompareHoverTargetSignature(target) {
    if (!target) {
        return "null";
    }
    const point = target.viewportPoint;
    const anchor = target.anchorRect;
    return [
        target.mode || "",
        target.kind || "",
        asKey(target.item?.id) || "",
        getMatchKey(target.match) || "",
        anchor?.left ?? "",
        anchor?.top ?? "",
        anchor?.right ?? "",
        anchor?.bottom ?? "",
        point?.x ?? "",
        point?.y ?? "",
        target.trackRowHeight ?? "",
        target.publicationReview ? "publication" : "live",
        target.transactionSummaryLoading ? "transaction-loading" : "",
        target.transactionSummaryError ? "transaction-error" : "",
        getTransactionSummarySignature(target.transactionSummary),
    ].join("|");
}

function areHoverFocusEqual(left, right) {
    if (!left || !right) {
        return left === right;
    }
    return left.origin === right.origin
        && left.itemId === right.itemId
        && left.matchKey === right.matchKey
        && left.editingFlightId === right.editingFlightId
        && left.referenceSourceId === right.referenceSourceId
        && left.statusLabel === right.statusLabel
        && left.statusDescription === right.statusDescription
        && left.title === right.title;
}

function isItemRelatedToHoverFocus(item, hoverFocus) {
    if (!item || !hoverFocus) {
        return false;
    }

    const role = item?.meta?.role;
    const itemId = asKey(item?.id);
    const matchKey = getMatchKey(item?.meta?.match);
    const editingFlightId = role === "editing" ? asKey(item?.meta?.flight?.id) : null;
    const referenceSourceId = role === "reference" ? asKey(item?.meta?.sourceId) : null;

    return Boolean(
        (hoverFocus.itemId && itemId === hoverFocus.itemId)
            || (hoverFocus.matchKey && matchKey === hoverFocus.matchKey)
            || (hoverFocus.editingFlightId && editingFlightId === hoverFocus.editingFlightId)
            || (hoverFocus.referenceSourceId && referenceSourceId === hoverFocus.referenceSourceId),
    );
}

function getItemHoverClass(item, hoverFocus) {
    if (!hoverFocus) {
        return null;
    }
    if (!isItemRelatedToHoverFocus(item, hoverFocus)) {
        return "schedule-draft-timeline-item-hover-muted";
    }
    return hoverFocus.itemId && asKey(item?.id) === hoverFocus.itemId
        ? "schedule-draft-timeline-item-hover-active"
        : "schedule-draft-timeline-item-hover-related";
}

function isLinkRelatedToHoverFocus(link, hoverFocus) {
    if (!link || !hoverFocus) {
        return false;
    }
    if (hoverFocus.matchKey && link.matchKey === hoverFocus.matchKey) {
        return true;
    }
    return Boolean(
        hoverFocus.editingFlightId
            && hoverFocus.referenceSourceId
            && link.draftFlightId === hoverFocus.editingFlightId
            && link.sourceId === hoverFocus.referenceSourceId,
    );
}

function getLinkHoverClass(link, hoverFocus) {
    if (!hoverFocus) {
        return "";
    }
    return isLinkRelatedToHoverFocus(link, hoverFocus)
        ? "schedule-draft-match-link-hover-active"
        : "schedule-draft-match-link-hover-muted";
}

function getStatusLabel(match, unmatchedLabel = "待处理", { publicationReview = false } = {}) {
    if (!match) {
        return unmatchedLabel;
    }
    const status = getMatchStatus(match, { publicationReview });
    const statusLabel = getMatchStatusDisplayLabel(status, { publicationReview });
    const matchType = getMatchType(match);
    return matchType && !publicationReview ? `${getMatchTypeDisplayLabel(matchType)}/${statusLabel}` : statusLabel;
}

export function getTimelineCompareMatchStatusLabel(match, { publicationReview = false } = {}) {
    return getStatusLabel(match, publicationReview ? "发布快照缺失" : "待处理", { publicationReview });
}

function getStatusDescription(status, role, { publicationReview = false } = {}) {
    if (publicationReview) {
        if (status === "MATCHED") {
            return "发布时已匹配并替换原执行航班";
        }
        if (status === "NEW") {
            return "发布时新增执行航班";
        }
        if (status === "CANCELLED") {
            return "发布时取消原执行航班";
        }
        if (status === "IGNORE") {
            return role === "reference" ? "发布时忽略参考侧执行航班" : "发布时忽略编辑侧航班";
        }
        return "发布快照或匹配事实不完整";
    }
    if (status === "MATCHED" || status === "INVALID") {
        return status === "INVALID" ? "匹配已失效，需重新处理" : "已与另一侧航班建立匹配";
    }
    if (status === "NEW") {
        return "编辑侧新增，发布时创建新执行航班";
    }
    if (status === "CANCELLED") {
        return "参考侧取消，发布时取消旧执行航班";
    }
    if (status === "IGNORE") {
        return role === "reference" ? "参考侧忽略，本次发布不取消旧航班" : "编辑侧忽略，本次发布不创建新航班";
    }
    return "待处理状态，发布前需要人工确认";
}

function getItemClassName({ match, pending, referenceMuted, role, publicationReview }) {
    const status = getMatchStatus(match, { publicationReview });
    const sourceTone = match ? getMatchSourceTone(match) : null;
    return [
        referenceMuted ? "schedule-draft-timeline-item-reference-muted" : null,
        pending ? "schedule-draft-timeline-item-pending-match" : null,
        role ? `schedule-draft-timeline-item-compare-role-${role}` : null,
        sourceTone ? `schedule-draft-timeline-item-match-source-${sourceTone}` : null,
        match ? `schedule-draft-timeline-item-match-${status.toLowerCase()}` : "schedule-draft-timeline-item-match-unmatched",
    ].filter(Boolean).join(" ");
}

function getItemDiffStatus(match, { publicationReview = false } = {}) {
    const status = getMatchStatus(match, { publicationReview });
    if (status === "NEW") {
        return "added";
    }
    if (status === "CANCELLED") {
        return "deleted";
    }
    if ((!publicationReview && status === "INVALID") || status === "MATCHED") {
        return "modified";
    }
    return null;
}

function buildMatchMaps(matches = []) {
    const matchByDraftFlightId = new Map();
    const matchBySourceId = new Map();

    const chooseMatch = (current, next) => {
        if (!current) {
            return next;
        }
        if (getMatchType(next) === "MANUAL" && getMatchType(current) !== "MANUAL") {
            return next;
        }
        return current;
    };

    toArray(matches).forEach((match) => {
        const draftFlightId = asKey(match?.schedule_draft_flight_id);
        const sourceId = getMatchSourceId(match);
        if (draftFlightId) {
            matchByDraftFlightId.set(draftFlightId, chooseMatch(matchByDraftFlightId.get(draftFlightId), match));
        }
        if (sourceId) {
            matchBySourceId.set(sourceId, chooseMatch(matchBySourceId.get(sourceId), match));
        }
    });

    return { matchByDraftFlightId, matchBySourceId };
}

function getSortTime(value) {
    const timestamp = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function sortCompareFlights(flights = [], useEffectiveTime = false) {
    return [...flights].sort((left, right) => {
        const leftTimeInfo = getFlightTimeDisplayInfo(left, { useEffectiveTime });
        const rightTimeInfo = getFlightTimeDisplayInfo(right, { useEffectiveTime });
        return getSortTime(leftTimeInfo.departureTime || leftTimeInfo.arrivalTime) - getSortTime(rightTimeInfo.departureTime || rightTimeInfo.arrivalTime);
    });
}

function buildFlightItem({ id, trackId, flight, variant, match, pending, sourceLabel, referenceMuted = false, role, sourceId, useEffectiveTime = false, publicationReview = false }) {
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    const timeLabels = buildItemTimeLabels(flight, { useEffectiveTime });
    const status = getMatchStatus(match, { publicationReview });
    return {
        id,
        trackId,
        title: normalizeString(flight?.flight_number, "未命名航班"),
        subtitle: timeLabels.departure,
        departureLabel: timeLabels.departure,
        arrivalLabel: timeLabels.arrival,
        start: timeInfo.departureTime,
        end: timeInfo.arrivalTime || timeInfo.departureTime,
        variant,
        diffStatus: getItemDiffStatus(match, { publicationReview }),
        className: getItemClassName({ match, pending, referenceMuted, role, publicationReview }),
        badges: [{ text: getStatusLabel(match, publicationReview ? "发布快照缺失" : "待处理", { publicationReview }), tone: `match-${status.toLowerCase()}` }],
        meta: {
            role,
            flight,
            match,
            sourceId,
            sourceLabel,
            trackKind: variant,
            usesEffectiveTime: useEffectiveTime,
            timeInfo,
            timeTooltipLines: buildItemTimeTooltipLines(flight, { useEffectiveTime }),
            publicationReview,
        },
    };
}

function buildCompareModel({ editingTrack, referenceTrack, matches, pendingEditingFlightId, publicationReview = false, airportConstraintsMap = {} }) {
    const editingFlights = sortCompareFlights(getTrackFlights(editingTrack), false);
    const referenceFlights = sortCompareFlights(getTrackFlights(referenceTrack), true);
    const { matchByDraftFlightId, matchBySourceId } = buildMatchMaps(matches);
    const editingLabel = editingTrack ? getTrackDisplayName(editingTrack) : "编辑轨";
    const referenceLabel = referenceTrack ? getTrackDisplayName(referenceTrack) : "参考轨";

    const tracks = [
        {
            id: REFERENCE_TRACK_ID,
            kind: "compare-reference",
            role: "reference",
            label: `${publicationReview ? "发布时执行基线" : "参考"} · ${referenceLabel}`,
            subtitle: `${referenceFlights.length} 个航班`,
            sourceLabel: referenceTrack?.name || "参考轨",
            context: publicationReview ? "发布后回看读取发布时执行基线快照，不读取实时执行航班。" : "匹配审查参考侧优先使用预计起降时间；实际起降时间不参与编排判断。",
            badges: [{ text: publicationReview ? "发布快照" : formatTrackTypeLabel(referenceTrack?.track_type, "参考轨"), tone: "kind-track" }],
        },
        {
            id: COMPARE_CORRIDOR_TRACK_ID,
            kind: "compare-corridor",
            role: "connector",
            label: "",
            subtitle: "",
            sourceLabel: "",
            context: "参考执行轨与编辑草稿轨之间的匹配连线层。",
            badges: [],
        },
        {
            id: EDITING_TRACK_ID,
            kind: "editable",
            role: "editing",
            label: `${publicationReview ? "发布时编辑轨" : "编辑"} · ${editingLabel}`,
            subtitle: `${editingFlights.length} 个航班`,
            sourceLabel: editingTrack?.name || "编辑轨",
            badges: [{ text: publicationReview ? "已发布方案" : "当前编辑方案", tone: "kind-editable" }],
        },
    ];

    const editingItems = editingFlights.map((flight) => {
        const flightId = asKey(flight?.id);
        const match = matchByDraftFlightId.get(flightId);
        return buildFlightItem({
            id: getEditingItemId(flightId),
            trackId: EDITING_TRACK_ID,
            flight,
            variant: "editable",
            match,
            pending: flightId && flightId === pendingEditingFlightId,
            sourceLabel: editingLabel,
            role: "editing",
            sourceId: null,
            publicationReview,
        });
    });

    const referenceItems = referenceFlights.map((flight, index) => {
        const sourceId = getReferenceFlightSourceId(flight) || `${flight?.id || index}`;
        const match = matchBySourceId.get(sourceId);
        return buildFlightItem({
            id: getReferenceItemId(sourceId),
            trackId: REFERENCE_TRACK_ID,
            flight,
            variant: "reference",
            match,
            pending: false,
            sourceLabel: referenceLabel,
            referenceMuted: true,
            role: "reference",
            sourceId,
            useEffectiveTime: true,
            publicationReview,
        });
    });

    const timelineItems = buildTimelineVisualItems([...referenceItems, ...editingItems], { groundSelectable: false, airportConstraintsMap });
    const timeWindowFlights = timelineItems.filter((item) => item.itemType !== "ground").map((item) => ({ std: item.start, sta: item.end }));
    return {
        tracks,
        items: timelineItems,
        layout: {
            minHeight: 240,
            trackRowHeight: 64,
        },
        timeWindow: getTimeWindowFromFlights(timeWindowFlights, null),
        emptyState: timeWindowFlights.length
            ? null
            : {
                  title: "当前对比轨没有航班",
                  description: "编辑轨或参考轨为空，无法建立航班匹配。",
              },
    };
}

function buildPath(start, end) {
    const middleY = start.y + (end.y - start.y) / 2;
    return `M ${start.x} ${start.y} C ${start.x} ${middleY}, ${end.x} ${middleY}, ${end.x} ${end.y}`;
}

function getRectSize(rect, axis) {
    const size = Number(rect?.[axis]);
    if (Number.isFinite(size)) {
        return size;
    }
    const start = axis === "width" ? Number(rect?.left) : Number(rect?.top);
    const end = axis === "width" ? Number(rect?.right) : Number(rect?.bottom);
    return Number.isFinite(start) && Number.isFinite(end) ? Math.max(end - start, 0) : 0;
}

function hasMeasurableRect(rect) {
    return getRectSize(rect, "width") > 0 && getRectSize(rect, "height") > 0;
}

function isDegenerateLinkPath(start, end) {
    return !Number.isFinite(start?.x)
        || !Number.isFinite(start?.y)
        || !Number.isFinite(end?.x)
        || !Number.isFinite(end?.y)
        || (start.x === end.x && start.y === end.y);
}

function getCompareTimelineElementItemId(element) {
    const contentElement = element?.querySelector?.("[data-timeline-item-id]");
    return element?.["vis-item"]?.id
        ?? element?.["vis-item"]?.data?.id
        ?? element?.dataset?.id
        ?? element?.dataset?.timelineItemId
        ?? element?.getAttribute?.("data-id")
        ?? element?.getAttribute?.("data-timeline-item-id")
        ?? contentElement?.dataset?.timelineItemId
        ?? contentElement?.getAttribute?.("data-timeline-item-id")
        ?? null;
}

function getLinkLayoutSignature(links = []) {
    return links.map((link) => [
        link.id,
        link.d,
        Math.round(link.labelX * 10) / 10,
        Math.round(link.labelY * 10) / 10,
        link.sourceTone,
        link.diffTone,
        link.label || "",
        getMatchStatus(link.match),
        getMatchType(link.match),
    ].join("~")).join("|");
}

function updateItemHoverClass(element, previousClassName, nextClassName) {
    if (previousClassName === nextClassName) {
        if (!nextClassName && ITEM_HOVER_CLASSES.some((className) => element.classList.contains(className))) {
            ITEM_HOVER_CLASSES.forEach((className) => element.classList.remove(className));
        }
        return;
    }
    if (previousClassName) {
        element.classList.remove(previousClassName);
    } else if (nextClassName) {
        ITEM_HOVER_CLASSES.forEach((className) => element.classList.remove(className));
    }
    if (nextClassName) {
        element.classList.add(nextClassName);
    }
}

const TimelineCompareView = forwardRef(({ editingTrack, referenceTrack, matches = EMPTY_MATCHES, readOnly = false, publicationReview = false, transactionSummaries = null, transactionSummaryLoading = false, transactionSummaryError = null, airportConstraintsMap = EMPTY_AIRPORT_CONSTRAINTS_MAP, onMatchCreate, onMatchDelete, onMarkNew, onMarkCancelled, onMarkIgnore, onHoverTargetChange, viewportBinding }, ref) => {
    const shellRef = useRef(null);
    const overlayRef = useRef(null);
    const measureRafRef = useRef(null);
    const measureSettleTimerIdsRef = useRef([]);
    const layoutRetryTimerRef = useRef(null);
    const layoutRetrySignatureRef = useRef(null);
    const layoutRetryCountRef = useRef(0);
    const operationPendingRef = useRef(false);
    const pendingEditingFlightIdRef = useRef(null);
    const editingFlightByIdRef = useRef(new Map());
    const scheduleMeasureRef = useRef(() => {});
    const measureLinksRef = useRef(() => {});
    const refreshCurrentHoverTargetRef = useRef(() => {});
    const onMatchCreateRef = useRef(onMatchCreate);
    const lastHoverTargetSignatureRef = useRef("null");
    const currentHoverTargetRef = useRef(null);
    const hoverClassByItemIdRef = useRef(new Map());
    const linkLayoutSignatureRef = useRef("");
    const linksRef = useRef([]);
    const overlaySizeSignatureRef = useRef("0:0");
    const lastTransactionContextSignatureRef = useRef(null);
    const [pendingEditingFlightId, setPendingEditingFlightId] = useState(null);
    const [links, setLinks] = useState([]);
    const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
    const [contextMenu, setContextMenu] = useState(null);
    const [operationPending, setOperationPending] = useState(false);
    const [hoverFocus, setHoverFocus] = useState(null);

    const trackMatches = useMemo(
        () => toArray(matches).filter((match) => {
            if (!editingTrack?.id || !referenceTrack?.id) {
                return false;
            }
            const matchTrackId = asKey(match?.track_id);
            const matchReferenceTrackId = getMatchReferenceTrackId(match);
            if (matchTrackId && matchTrackId !== String(editingTrack.id)) {
                return false;
            }
            return !matchReferenceTrackId || matchReferenceTrackId === String(referenceTrack.id);
        }),
        [editingTrack?.id, matches, referenceTrack?.id],
    );

    const matchMaps = useMemo(() => buildMatchMaps(trackMatches), [trackMatches]);

    const compareModel = useMemo(
        () => buildCompareModel({ editingTrack, referenceTrack, matches: trackMatches, pendingEditingFlightId, publicationReview, airportConstraintsMap }),
        [airportConstraintsMap, editingTrack, pendingEditingFlightId, publicationReview, referenceTrack, trackMatches],
    );
    const hoverTrackRowHeight = compareModel.layout?.trackRowHeight;

    const itemById = useMemo(() => new Map((compareModel.items || []).map((item) => [String(item.id), item])), [compareModel.items]);

    const transactionSummaryBySourceId = useMemo(() => normalizeTransactionSummaryMap(transactionSummaries), [transactionSummaries]);
    const transactionContextSignature = useMemo(() => [
        transactionSummaryLoading ? "loading" : "idle",
        transactionSummaryError ? "error" : "ok",
        getTransactionSummaryMapSignature(transactionSummaryBySourceId),
    ].join("|"), [transactionSummaryBySourceId, transactionSummaryError, transactionSummaryLoading]);

    const editingFlightById = useMemo(() => {
        const next = new Map();
        (compareModel.items || []).forEach((item) => {
            if (item?.meta?.role === "editing" && item.meta.flight?.id) {
                next.set(String(item.meta.flight.id), item.meta.flight);
            }
        });
        return next;
    }, [compareModel.items]);

    useEffect(() => {
        pendingEditingFlightIdRef.current = pendingEditingFlightId;
    }, [pendingEditingFlightId]);

    useEffect(() => {
        editingFlightByIdRef.current = editingFlightById;
    }, [editingFlightById]);

    useEffect(() => {
        onMatchCreateRef.current = onMatchCreate;
    }, [onMatchCreate]);

    const emitHoverTargetChange = useCallback((nextTarget) => {
        currentHoverTargetRef.current = nextTarget || null;
        const nextSignature = getCompareHoverTargetSignature(nextTarget);
        if (nextSignature === lastHoverTargetSignatureRef.current) {
            return;
        }
        lastHoverTargetSignatureRef.current = nextSignature;
        onHoverTargetChange?.(nextTarget);
    }, [onHoverTargetChange]);

    useEffect(() => () => {
        lastHoverTargetSignatureRef.current = "null";
        currentHoverTargetRef.current = null;
        onHoverTargetChange?.(null);
        if (measureRafRef.current) {
            cancelAnimationFrame(measureRafRef.current);
            measureRafRef.current = null;
        }
        measureSettleTimerIdsRef.current.forEach((timerId) => window.clearTimeout(timerId));
        measureSettleTimerIdsRef.current = [];
        if (layoutRetryTimerRef.current) {
            window.clearTimeout(layoutRetryTimerRef.current);
            layoutRetryTimerRef.current = null;
        }
    }, [onHoverTargetChange]);

    const runOperation = useCallback(async (operation) => {
        if (operationPendingRef.current || !operation) {
            return;
        }
        operationPendingRef.current = true;
        setOperationPending(true);
        try {
            await operation();
        } catch {
            // Mutation hooks surface user-facing errors; keep the compare view responsive.
        } finally {
            operationPendingRef.current = false;
            setOperationPending(false);
        }
    }, []);

    const getItemElementMap = useCallback(() => {
        const shell = shellRef.current;
        if (!shell) {
            return new Map();
        }

        return Array.from(shell.querySelectorAll(".vis-item.schedule-draft-timeline-item")).reduce((accumulator, element) => {
            const itemId = getCompareTimelineElementItemId(element);
            if (itemId != null) {
                accumulator.set(String(itemId), element);
            }
            return accumulator;
        }, new Map());
    }, []);

    const clearLayoutRetry = useCallback(() => {
        if (layoutRetryTimerRef.current) {
            window.clearTimeout(layoutRetryTimerRef.current);
            layoutRetryTimerRef.current = null;
        }
        layoutRetrySignatureRef.current = null;
        layoutRetryCountRef.current = 0;
    }, []);

    const scheduleLayoutRetry = useCallback((signature) => {
        if (!signature) {
            return;
        }
        if (layoutRetrySignatureRef.current !== signature) {
            layoutRetrySignatureRef.current = signature;
            layoutRetryCountRef.current = 0;
        }
        if (layoutRetryTimerRef.current || layoutRetryCountRef.current >= COMPARE_LAYOUT_RETRY_LIMIT) {
            return;
        }
        layoutRetryTimerRef.current = window.setTimeout(() => {
            layoutRetryTimerRef.current = null;
            layoutRetryCountRef.current += 1;
            scheduleMeasureRef.current?.();
        }, COMPARE_LAYOUT_RETRY_DELAY_MS);
    }, []);

    const measureLinks = useCallback(() => {
        const shell = shellRef.current;
        if (!shell) {
            linkLayoutSignatureRef.current = "";
            linksRef.current = [];
            overlaySizeSignatureRef.current = "0:0";
            clearLayoutRetry();
            setLinks((current) => (current.length ? [] : current));
            setOverlaySize((current) => (current.width || current.height ? { width: 0, height: 0 } : current));
            return;
        }

        const itemElements = getItemElementMap();
        const shellRect = shell.getBoundingClientRect();
        const lineMatches = trackMatches.filter((match) => isLineMatch(match, { publicationReview }));
        const overlayWidth = Math.max(shell.clientWidth, shell.scrollWidth, Math.ceil(shellRect.width || 0));
        const overlayHeight = Math.max(shell.clientHeight, shell.scrollHeight, Math.ceil(shellRect.height || 0));
        const measureIssues = [];
        let needsLayoutRetry = Boolean(lineMatches.length && (!overlayWidth || !overlayHeight));
        if (needsLayoutRetry) {
            measureIssues.push("overlay-size");
        }
        const nextLinks = lineMatches.map((match) => {
            const draftFlightId = getMatchDraftFlightId(match);
            const sourceId = getMatchSourceId(match);
            const matchKey = getMatchKey(match);
            const editingElement = itemElements.get(getEditingItemId(draftFlightId));
            const referenceElement = itemElements.get(getReferenceItemId(sourceId));
            if (!editingElement || !referenceElement) {
                needsLayoutRetry = true;
                measureIssues.push(!editingElement ? `missing-editing:${draftFlightId}` : `missing-reference:${sourceId}`);
                return null;
            }

            const editingRect = editingElement.getBoundingClientRect();
            const referenceRect = referenceElement.getBoundingClientRect();
            if (!hasMeasurableRect(editingRect) || !hasMeasurableRect(referenceRect)) {
                needsLayoutRetry = true;
                measureIssues.push("zero-item-rect");
            }
            const start = {
                x: referenceRect.left + referenceRect.width / 2 - shellRect.left,
                y: referenceRect.bottom - shellRect.top,
            };
            const end = {
                x: editingRect.left + editingRect.width / 2 - shellRect.left,
                y: editingRect.top - shellRect.top,
            };
            if (isDegenerateLinkPath(start, end)) {
                needsLayoutRetry = true;
                measureIssues.push("degenerate-path");
            }
            const editingItem = itemById.get(getEditingItemId(draftFlightId));
            const referenceItem = itemById.get(getReferenceItemId(sourceId));
            const transactionSummary = transactionSummaryBySourceId.get(sourceId) || null;
            const diff = buildTimelineCompareLinkDiff(match, editingItem?.meta?.flight, referenceItem?.meta?.flight, transactionSummary, { publicationReview });

            return {
                id: matchKey,
                matchKey,
                draftFlightId,
                sourceId,
                d: buildPath(start, end),
                labelX: (start.x + end.x) / 2,
                labelY: start.y + (end.y - start.y) / 2,
                sourceTone: getMatchSourceTone(match),
                diffTone: diff.tone,
                label: diff.label,
                match,
            };
        }).filter(Boolean);

        if (lineMatches.length && (needsLayoutRetry || nextLinks.length < lineMatches.length)) {
            scheduleLayoutRetry(lineMatches.map(getMatchKey).join("|"));
        } else {
            clearLayoutRetry();
        }

        if (trackMatches.length || lineMatches.length || needsLayoutRetry) {
            debugScheduleDraftTimeline("compare-link-measure", {
                trackMatchCount: trackMatches.length,
                lineMatchCount: lineMatches.length,
                linkCount: nextLinks.length,
                itemElementCount: itemElements.size,
                itemElementIds: Array.from(itemElements.keys()).slice(0, 12),
                overlayWidth,
                overlayHeight,
                needsLayoutRetry,
                measureIssues: Array.from(new Set(measureIssues)).slice(0, 8),
                matchStatusSample: trackMatches.slice(0, 6).map((match) => ({
                    key: getMatchKey(match),
                    trackId: match?.track_id,
                    referenceTrackId: match?.reference_track_id,
                    draftFlightId: getMatchDraftFlightId(match),
                    sourceId: getMatchSourceId(match),
                    matchStatus: getRawMatchStatus(match),
                    effectiveStatus: getMatchStatus(match, { publicationReview }),
                })),
            });
        }

        const nextOverlaySize = {
            width: Math.max(overlayWidth, 1),
            height: Math.max(overlayHeight, 1),
        };
        const nextOverlaySizeSignature = `${nextOverlaySize.width}:${nextOverlaySize.height}`;
        if (overlaySizeSignatureRef.current !== nextOverlaySizeSignature) {
            overlaySizeSignatureRef.current = nextOverlaySizeSignature;
            setOverlaySize(nextOverlaySize);
        }

        const nextLinkLayoutSignature = getLinkLayoutSignature(nextLinks);
        if (linkLayoutSignatureRef.current !== nextLinkLayoutSignature) {
            linkLayoutSignatureRef.current = nextLinkLayoutSignature;
            linksRef.current = nextLinks;
            setLinks(nextLinks);
        }
    }, [clearLayoutRetry, getItemElementMap, itemById, publicationReview, scheduleLayoutRetry, trackMatches, transactionSummaryBySourceId]);

    useEffect(() => {
        measureLinksRef.current = measureLinks;
    }, [measureLinks]);

    const getTransactionHoverContext = useCallback((sourceId) => buildTransactionHoverContext(
        sourceId,
        transactionSummaryBySourceId,
        transactionSummaryLoading,
        transactionSummaryError,
    ), [transactionSummaryBySourceId, transactionSummaryError, transactionSummaryLoading]);

    const refreshCurrentHoverTarget = useCallback(() => {
        const currentTarget = currentHoverTargetRef.current;
        if (!currentTarget) {
            return;
        }

        let nextTarget = null;
        if (currentTarget.kind === "link" && currentTarget.match) {
            const currentMatchKey = getMatchKey(currentTarget.match);
            const match = trackMatches.find((candidate) => getMatchKey(candidate) === currentMatchKey) || currentTarget.match;
            nextTarget = buildCompareHoverTargetFromMatch(match, itemById, getTransactionHoverContext(getMatchSourceId(match)));
        } else if (currentTarget.item?.id) {
            const item = itemById.get(String(currentTarget.item.id));
            const sourceId = getMatchSourceId(item?.meta?.match) || (item?.meta?.role === "reference" ? item?.meta?.sourceId : null);
            nextTarget = item ? buildCompareHoverTargetFromItem(item, itemById, getTransactionHoverContext(sourceId)) : null;
        }

        if (!nextTarget) {
            emitHoverTargetChange(null);
            return;
        }

        const itemElements = getItemElementMap();
        const anchorElement = nextTarget.item?.id ? itemElements.get(String(nextTarget.item.id)) || null : null;
        emitHoverTargetChange(withHoverViewportContext(nextTarget, null, hoverTrackRowHeight, anchorElement));
    }, [emitHoverTargetChange, getItemElementMap, getTransactionHoverContext, hoverTrackRowHeight, itemById, trackMatches]);

    useEffect(() => {
        refreshCurrentHoverTargetRef.current = refreshCurrentHoverTarget;
    }, [refreshCurrentHoverTarget]);

    const refreshTransactionDerivedLinkState = useCallback(() => {
        const currentLinks = linksRef.current;
        if (!currentLinks.length) {
            scheduleMeasureRef.current?.();
            return;
        }

        const nextLinks = currentLinks.map((link) => {
            const editingItem = itemById.get(getEditingItemId(link.draftFlightId));
            const referenceItem = itemById.get(getReferenceItemId(link.sourceId));
            const transactionSummary = transactionSummaryBySourceId.get(link.sourceId) || null;
            const diff = buildTimelineCompareLinkDiff(link.match, editingItem?.meta?.flight, referenceItem?.meta?.flight, transactionSummary, { publicationReview });
            return {
                ...link,
                diffTone: diff.tone,
                label: diff.label,
            };
        });
        const nextLinkLayoutSignature = getLinkLayoutSignature(nextLinks);
        if (linkLayoutSignatureRef.current === nextLinkLayoutSignature) {
            return;
        }
        linkLayoutSignatureRef.current = nextLinkLayoutSignature;
        linksRef.current = nextLinks;
        setLinks(nextLinks);
    }, [itemById, publicationReview, transactionSummaryBySourceId]);

    const scheduleMeasure = useCallback(() => {
        if (measureRafRef.current) {
            return;
        }
        measureRafRef.current = requestAnimationFrame(() => {
            measureRafRef.current = null;
            measureLinksRef.current?.();
            refreshCurrentHoverTargetRef.current?.();
        });
    }, []);

    useEffect(() => {
        scheduleMeasureRef.current = scheduleMeasure;
    }, [scheduleMeasure]);

    useLayoutEffect(() => {
        clearLayoutRetry();
        scheduleMeasure();
        measureSettleTimerIdsRef.current.forEach((timerId) => window.clearTimeout(timerId));
        measureSettleTimerIdsRef.current = COMPARE_MEASURE_SETTLE_DELAYS_MS.map((delayMs) => window.setTimeout(scheduleMeasure, delayMs));
        return () => {
            measureSettleTimerIdsRef.current.forEach((timerId) => window.clearTimeout(timerId));
            measureSettleTimerIdsRef.current = [];
        };
    }, [clearLayoutRetry, compareModel, scheduleMeasure, trackMatches]);

    useEffect(() => {
        if (lastTransactionContextSignatureRef.current === transactionContextSignature) {
            return;
        }
        lastTransactionContextSignatureRef.current = transactionContextSignature;
        refreshTransactionDerivedLinkState();
        refreshCurrentHoverTargetRef.current?.();
    }, [refreshTransactionDerivedLinkState, transactionContextSignature]);

    useLayoutEffect(() => {
        const shell = shellRef.current;
        if (!shell) {
            return undefined;
        }

        const handleLayoutChange = () => scheduleMeasure();
        const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(handleLayoutChange) : null;
        const mutationObserver = typeof MutationObserver !== "undefined" ? new MutationObserver(handleLayoutChange) : null;
        resizeObserver?.observe(shell);
        mutationObserver?.observe(shell, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class", "style", "data-id", "data-timeline-item-id"],
        });
        shell.addEventListener("scroll", handleLayoutChange, true);
        shell.addEventListener("wheel", handleLayoutChange, true);
        window.addEventListener("resize", handleLayoutChange);
        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            shell.removeEventListener("scroll", handleLayoutChange, true);
            shell.removeEventListener("wheel", handleLayoutChange, true);
            window.removeEventListener("resize", handleLayoutChange);
        };
    }, [scheduleMeasure]);

    const findItemElementFromEventTarget = useCallback((target) => target?.closest?.(".vis-item.schedule-draft-timeline-item") || null, []);

    const getItemFromElement = useCallback((element) => {
        const itemId = getCompareTimelineElementItemId(element);
        return itemId == null ? null : itemById.get(String(itemId)) || null;
    }, [itemById]);

    const findItemFromEventTarget = useCallback((target) => getItemFromElement(findItemElementFromEventTarget(target)), [findItemElementFromEventTarget, getItemFromElement]);

    const setHoverFocusIfChanged = useCallback((nextFocus) => {
        setHoverFocus((current) => (areHoverFocusEqual(current, nextFocus) ? current : nextFocus));
    }, []);

    useEffect(() => {
        const rafId = requestAnimationFrame(() => {
            const itemElements = getItemElementMap();
            const nextClassByItemId = new Map();
            itemElements.forEach((element, itemId) => {
                const hoverClass = getItemHoverClass(itemById.get(itemId), hoverFocus);
                if (hoverClass) {
                    nextClassByItemId.set(itemId, hoverClass);
                }
                updateItemHoverClass(element, hoverClassByItemIdRef.current.get(itemId) || null, hoverClass || null);
            });
            hoverClassByItemIdRef.current = nextClassByItemId;
        });

        return () => cancelAnimationFrame(rafId);
    }, [getItemElementMap, hoverFocus, itemById]);

    const handleItemMouseOver = useCallback((event) => {
        const element = findItemElementFromEventTarget(event.target);
        if (!element || (event.relatedTarget && element.contains(event.relatedTarget))) {
            return;
        }
        const item = getItemFromElement(element);
        if (item) {
            const sourceId = getMatchSourceId(item?.meta?.match) || (item?.meta?.role === "reference" ? item?.meta?.sourceId : null);
            setHoverFocusIfChanged(buildHoverFocusFromItem(item));
            emitHoverTargetChange(withHoverViewportContext(buildCompareHoverTargetFromItem(item, itemById, getTransactionHoverContext(sourceId)), event, hoverTrackRowHeight, element));
        }
    }, [emitHoverTargetChange, findItemElementFromEventTarget, getItemFromElement, getTransactionHoverContext, hoverTrackRowHeight, itemById, setHoverFocusIfChanged]);

    const handleItemMouseOut = useCallback((event) => {
        const element = findItemElementFromEventTarget(event.target);
        if (!element || (event.relatedTarget && element.contains(event.relatedTarget))) {
            return;
        }
        setHoverFocus((current) => (current?.origin === "item" ? null : current));
        emitHoverTargetChange(null);
    }, [emitHoverTargetChange, findItemElementFromEventTarget]);

    const handleLinkMouseEnter = useCallback((event, match) => {
        setHoverFocusIfChanged(buildHoverFocusFromMatch(match));
        emitHoverTargetChange(withHoverViewportContext(buildCompareHoverTargetFromMatch(match, itemById, getTransactionHoverContext(getMatchSourceId(match))), event, hoverTrackRowHeight));
    }, [emitHoverTargetChange, getTransactionHoverContext, hoverTrackRowHeight, itemById, setHoverFocusIfChanged]);

    const handleLinkMouseLeave = useCallback(() => {
        setHoverFocus((current) => (current?.origin === "link" ? null : current));
        emitHoverTargetChange(null);
    }, [emitHoverTargetChange]);

    const handleSelectItem = useCallback((item) => {
        if (readOnly) {
            return;
        }
        const role = item?.meta?.role;
        if (role === "editing") {
            const flightId = asKey(item.meta.flight?.id);
            if (!flightId) {
                return;
            }
            setPendingEditingFlightId((current) => (current === flightId ? null : flightId));
            return;
        }

        if (role === "reference" && pendingEditingFlightIdRef.current) {
            const editingFlight = editingFlightByIdRef.current.get(pendingEditingFlightIdRef.current);
            const referenceFlight = item.meta.flight;
            runOperation(async () => {
                await onMatchCreateRef.current?.(editingFlight, referenceFlight);
                setPendingEditingFlightId(null);
                scheduleMeasureRef.current?.();
            });
        }
    }, [readOnly, runOperation]);

    const handleContextMenu = useCallback((event) => {
        if (readOnly) {
            return;
        }
        const item = findItemFromEventTarget(event.target);
        if (!item?.meta?.role) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            item,
        });
    }, [findItemFromEventTarget, readOnly]);

    useEffect(() => {
        if (!contextMenu) {
            return undefined;
        }
        const close = () => setContextMenu(null);
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                close();
            }
        };
        window.addEventListener("click", close);
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("click", close);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [contextMenu]);

    const contextMatch = useMemo(() => {
        const item = contextMenu?.item;
        if (!item) {
            return null;
        }
        if (item.meta.role === "editing") {
            return matchMaps.matchByDraftFlightId.get(asKey(item.meta.flight?.id)) || null;
        }
        return matchMaps.matchBySourceId.get(asKey(item.meta.sourceId)) || null;
    }, [contextMenu, matchMaps]);

    const handleDeleteMatch = useCallback((match) => {
        if (!match?.id) {
            return;
        }
        if (!window.confirm("解除这条匹配关系？")) {
            return;
        }
        runOperation(() => onMatchDelete?.(match));
        setContextMenu(null);
    }, [onMatchDelete, runOperation]);

    const handleMarkNew = useCallback((flight) => {
        if (!flight?.id) {
            return;
        }
        if (!window.confirm(`将 ${flight.flight_number || "该航班"} 标记为新增？`)) {
            return;
        }
        runOperation(async () => {
            await onMarkNew?.(flight);
            setPendingEditingFlightId(null);
        });
        setContextMenu(null);
    }, [onMarkNew, runOperation]);

    const handleMarkCancelled = useCallback((flight) => {
        if (!getReferenceFlightSourceId(flight)) {
            return;
        }
        if (!window.confirm(`将 ${flight.flight_number || "该参考航班"} 标记为应取消？`)) {
            return;
        }
        runOperation(() => onMarkCancelled?.(flight));
        setContextMenu(null);
    }, [onMarkCancelled, runOperation]);

    const handleMarkIgnore = useCallback((item) => {
        if (!item?.meta?.role || !item?.meta?.flight) {
            return;
        }
        if (item.meta.role === "reference" && !getReferenceFlightSourceId(item.meta.flight)) {
            return;
        }
        if (!window.confirm(`忽略 ${item.meta.flight.flight_number || "该航班"} 的本次发布处理？`)) {
            return;
        }
        runOperation(async () => {
            await onMarkIgnore?.(item.meta.role, item.meta.flight);
            setPendingEditingFlightId(null);
        });
        setContextMenu(null);
    }, [onMarkIgnore, runOperation]);

    if (!editingTrack || !referenceTrack) {
        return (
            <div className="schedule-draft-compare-empty">
                <Empty description={publicationReview ? "发布快照不完整" : "缺少可对比的编辑轨或参考轨"} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                <Text type="secondary">{publicationReview ? "该发布记录缺少发布时编辑轨或执行基线快照。" : "请先为当前编辑轨绑定执行基线或只读参考轨。"}</Text>
            </div>
        );
    }

    return (
        <div
            ref={shellRef}
            className={`schedule-draft-compare-view ${operationPending ? "is-operating" : ""} ${hoverFocus ? "has-hover-focus" : ""}`}
            onContextMenu={handleContextMenu}
            onMouseOver={handleItemMouseOver}
            onMouseOut={handleItemMouseOut}
        >
            <MultitrackTimelineCanvas ref={ref} model={compareModel} onSelectItem={handleSelectItem} onLayoutChange={scheduleMeasure} viewportBinding={viewportBinding} itemPreviewTrigger="none" />
            <svg
                ref={overlayRef}
                className="schedule-draft-compare-overlay"
                viewBox={`0 0 ${overlaySize.width} ${overlaySize.height}`}
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                {links.map((link) => (
                    <g key={link.id} className={`schedule-draft-match-link-group ${getLinkHoverClass(link, hoverFocus)}`}>
                        <path
                            className="schedule-draft-match-link-hit"
                            d={link.d}
                            onMouseEnter={(event) => handleLinkMouseEnter(event, link.match)}
                            onMouseLeave={handleLinkMouseLeave}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (!readOnly) {
                                    handleDeleteMatch(link.match);
                                }
                            }}
                        />
                        <path className={`schedule-draft-match-link schedule-draft-match-link-source-${link.sourceTone} schedule-draft-match-link-diff-${link.diffTone}`} d={link.d} />
                        {link.label ? (
                            <text
                                className={`schedule-draft-match-link-label schedule-draft-match-link-label-${link.diffTone}`}
                                x={link.labelX}
                                y={link.labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                            >
                                {link.label}
                            </text>
                        ) : null}
                    </g>
                ))}
            </svg>
            {pendingEditingFlightId ? (
                <div className="schedule-draft-compare-hint">已选择草稿航班，点击上方执行航班即可建立人工匹配。</div>
            ) : null}
            {contextMenu ? (
                <div className="schedule-draft-compare-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={(event) => event.stopPropagation()}>
                    {contextMenu.item.meta.role === "editing" ? (
                        <button type="button" onClick={() => handleMarkNew(contextMenu.item.meta.flight)}>标记为新增</button>
                    ) : (
                        <button type="button" onClick={() => handleMarkCancelled(contextMenu.item.meta.flight)}>标记为应取消</button>
                    )}
                    <button type="button" onClick={() => handleMarkIgnore(contextMenu.item)}>忽略本次发布</button>
                    <button type="button" disabled={!contextMatch} onClick={() => handleDeleteMatch(contextMatch)}>解除匹配</button>
                </div>
            ) : null}
        </div>
    );
});

TimelineCompareView.displayName = "TimelineCompareView";

export default TimelineCompareView;
